import type { ParticipantSelection } from '../collaboration/selection';
/**
 * The 3D surface. A thin React wrapper: everything inside is imperative three.js, and the
 * component only forwards state changes and exposes a handle for the toolbar.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { type CameraPose, type DoorMarker, flippedResizeLabel, OPENING_DIM_OFFSET, openingChipPoint, type OpeningDimension, RESIZE_DIM_OFFSET, resizeChipRect, resizeDimPoint, resizeLabelRect, resizeLabelShift, Viewer, type ViewerGestureKind, type ViewerOverlay } from '../render/three/Viewer';
import { chromeRects } from './chrome';
import { publishViewZoom } from './viewZoom';
import { Icon } from './Icon';
import { useTouchScreen, WalkStick } from './WalkStick';
import { cursorStyle, handleResize, rotateGlyphAngle } from '../render/three/cursors';
import { chipText, hideGestureChip, itemChip, showGestureChip } from './gestureChip';
import { previewItemResize } from '../state/itemResize';
import { edgePoint, edgeSides, openingBox, previewOpeningEdge, type OpeningEdge } from '../state/openingResize';
import { OPENING_DIMENSION_LETTER, OPENING_DIMENSION_NAME, OPENING_EDGE_NAME, RESIZE_AXIS_LETTER, RESIZE_AXIS_NAME, releaseResizeHot, setResizeHot, useResizeHot } from './resizeHot';
import { store, type EditScope, type EditorState, type Selection } from '../state/store';
import { previewSceneItemTransform } from '../state/sceneGesture';
import { followHeldDrag } from '../state/planGesture';
import { readProjectView, type SavedCameras } from '../state/projectView';
import { contextSelection, nextSelectionOnTap, selectedItemIds, toggleItemSelection } from '../state/selectionTap';
import { useStore } from './useStore';
import type { PlanFurnitureLayer } from '../render/three/PlanTopView';
import type { Item, Level, Room, Vec2 } from '../core/hp/types';
import type { HydrationProgress } from '../render/three/HydrationQueue';
import type { ScreenPoint, ScreenRect } from '../state/itemReveal';
import type { WallPlanFrame, WallPlanInsets } from '../state/wallPlan';
import type { WallPlanMarks } from '../state/wallPlanSnap';
import type { SurfaceProbe } from '../core/geometry/stacking';
import type { LandingPointer } from '../core/geometry/landing';
import type { PresenceViewport } from '../collaboration/protocol';
import { t } from '../i18n/I18n';
import type { ContextPressResult } from './contextPress';

export interface SceneHandle {
  setParticipantSelections(values: readonly ParticipantSelection[]): void;
  participantViewport(): PresenceViewport | null;
  applyParticipantViewport(viewport: PresenceViewport): boolean;
  releaseParticipantViewport(): void;
  cancelInteraction(): void;
  planLayer(): PlanFurnitureLayer | null;
  /** The height of an item's loaded surface under plan points, for landing a dropped piece on it. */
  surfaceHeight: SurfaceProbe;
  placementPointer(clientX: number, clientY: number): LandingPointer | undefined;
  shelfPointAt(item: Item, clientX: number, clientY: number): Vec2 | null;
  recenter(): void;
  capture(): void;
  /** The current frame as a JPEG for uploads, long side capped. */
  snapshot(maxSide?: number, crop?: { x: number; y: number; width: number; height: number }): Promise<Blob | null>;
  /** The scene canvas, for mapping a CSS rect to canvas pixels. */
  canvasElement(): HTMLCanvasElement | null;
  setZoom(factor: number): void;
  floorPointAt(clientX: number, clientY: number): Vec2 | null;
  /** The wall face under the pointer, for a catalog piece put on a wall there (the wall plan's own face while it is open). */
  wallPointAt(clientX: number, clientY: number): { wallId: string; along: number; height: number; side: 'front' | 'back' } | null;
  worldPointAt(clientX: number, clientY: number): { x: number; y: number; z: number } | null;
  /** Where a floor point currently sits on screen, or null when it is off camera. */
  screenPointAt(point: Vec2, height?: number, bounds?: DOMRect): ScreenPoint | null;
  /** Bring a floor point into the free part of the stage. Returns whether the view moved. */
  revealPoint(point: Vec2, area: ScreenRect, height?: number): boolean;
  /** Where the camera is and looks, in plan space — for the minimap. */
  cameraPose(): CameraPose | null;
  /** Both 3D cameras as plain numbers, for storing where the project was left. */
  cameraState(): SavedCameras | null;
  /** The frame on screen as a card-sized JPEG, over the stage colour behind the canvas. */
  previewFrame(maxSide: number, background: string): Promise<Blob | null>;
  /** Called after every drawn frame; returns the unsubscribe. */
  onFrame(listener: () => void): () => void;
  navigateToRoom(room: Room): void;
  /** «Войти в комнату» through this door into `room`: the walk flies through it, the orbit pans there. */
  enterThroughDoor(openingId: string, room: Room): void;
  /** «Go up / Go down the stairs» (`Viewer.goStairs`): false when this floor has no stairs that way. */
  goStairs(direction: 'up' | 'down'): boolean;
  /** The wall plan's face-to-canvas mapping while it is open, for the dimension overlay. */
  wallPlanFrame(): WallPlanFrame | null;
  /** The selected item, what a move runs it into and the magnets' guides, for the wall plan's overlay. */
  wallPlanMarks(): WallPlanMarks | null;
  /** The wall plan back to the whole wall at zoom 1. */
  resetWallPlanView(): void;
  /** ⌘±, ⇧0 and the zoom menu on the wall plan and in the orbit (`Viewer.zoomView`). */
  zoomView(factor: number | 'reset'): void;
  /** How much canvas the side chrome takes while the wall plan is open; true when the picture moved. */
  setWallPlanInsets(insets: WallPlanInsets | null): boolean;
  /** A context press at a client point: any press in progress let go, what is there selected (`contextSelection`) unless editing is blocked. */
  contextPressAt(clientX: number, clientY: number): ContextPressResult;
}

/** What a tap or a context press on `hit` picks. */
function selectionOfHit(hit: { userData: Record<string, unknown> } | null, material?: { wallSide?: 'front' | 'back' }): Selection {
  const d = hit?.userData;
  return d?.kind === 'item' && typeof d.itemId === 'string' ? { kind: 'item', id: d.itemId }
    : d?.kind === 'opening' && typeof d.openingId === 'string' ? { kind: 'opening', id: d.openingId }
    : d?.kind === 'wall' && typeof d.wallId === 'string' ? { kind: 'wall', id: d.wallId, side: material?.wallSide ?? 'front' }
    : d?.kind === 'floor' && typeof d.roomIndex === 'number' ? { kind: 'room', index: d.roomIndex }
    // A plinth or a ceiling molding is an object of its own, as the app's WallPlinthController.
    : (d?.kind === 'plinth' || d?.kind === 'molding') && typeof d.wallId === 'string' ? { kind: 'trim', wallId: d.wallId, trim: d.kind }
    : null;
}

export const Scene3D = forwardRef<SceneHandle, { hidden: boolean; editingDisabled?: boolean;
  /** `items`: only items move; a selected wall, opening, room or trim stays where it is. */
  editScope?: EditScope; onNavigationIntent?: () => void;
  /** The walk climbed or went down a flight to floor `index`, which is now in view. */
  onWalkFloor?: (index: number) => void;
  /** Where the models' loading status goes: the end of the editor's column under the header, below the notices.
   * It shows on the plan too, where only the canvas hides; until the slot is there it waits. */
  statusSlot: HTMLElement | null }>(function Scene3D({ hidden, editingDisabled = false, editScope = 'all', onNavigationIntent, onWalkFloor, statusSlot }, ref) {
  const navigationIntent = useRef(onNavigationIntent); navigationIntent.current = onNavigationIntent;
  const walkFloor = useRef(onWalkFloor); walkFloor.current = onWalkFloor;
  const state = useStore(s => ({ showCatalog:s.showCatalog, showProperties:s.showProperties, materialTarget:s.materialTarget,
    showVariations:s.showVariations, showLighting:s.showLighting, showGallery:s.showGallery, cameraMode:s.cameraMode, showMinimap:s.showMinimap,
    wallMode:s.wallMode, firstPerson:s.firstPerson, selection:s.selection, mode:s.mode, planSurface:s.planSurface, wallView:s.wallView,
    resizeMode:s.resizeMode, isDragging:s.isDragging,
    selectedLocked: s.selection?.kind === 'item' && !!s.scene?.levels[s.activeLevelIndex]?.items.find(item => s.selection?.kind === 'item' && item.id === s.selection.id)?.locked, showWindowViews:s.showWindowViews, windowView:s.windowView, windowViewRotation:s.windowViewRotation, walkthrough:s.walkthrough }));
  const [stats, setStats] = useState<Record<string,number>>({});
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const viewerRef = useRef<Viewer | null>(null);
  const loadedFor = useRef<{ projectId: string; levelIndex: number } | null>(null);
  const cameraApplied = useRef<'orbit' | 'walk' | null>(null);
  const cameraProject = useRef<string | null>(null);
  const [progress, setProgress] = useState<HydrationProgress | null>(null);
  const [overlay, setOverlay] = useState<ViewerOverlay | null>(null);
  const [gesture, setGesture] = useState<ViewerGestureKind>(null);
  /** The demo walkthrough's door buttons, as the viewer finds the doorways in view. */
  const [doorMarkers, setDoorMarkers] = useState<DoorMarker[]>([]);
  // The catalog is not modal: with it open the scene and the wall plan still pick and move things, as the floor plan does.
  const touchScreen = useTouchScreen();
  const editBlocked = hidden || editingDisabled || state.showProperties || state.materialTarget !== null
    || state.showVariations || state.showLighting || state.showGallery || state.cameraMode || state.showMinimap || state.showWindowViews || state.walkthrough;
  const editBlockedRef = useRef(editBlocked); editBlockedRef.current = editBlocked;
  const itemsOnly = editScope === 'items';
  const dragsBlocked = editBlocked || (itemsOnly && state.selection?.kind !== 'item' && state.selection?.kind !== 'items');
  const dragsBlockedRef = useRef(dragsBlocked); dragsBlockedRef.current = dragsBlocked;
  const hiddenRef = useRef(hidden); hiddenRef.current = hidden;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const v = new Viewer(canvas);
    viewerRef.current = v;
    // Born knowing whether it is on screen: a 3D view hidden behind the plan asks for nothing only it would draw.
    v.setActive(!hiddenRef.current);
    const unsubscribeZoom = v.onFrame(() => { if (!hiddenRef.current) publishViewZoom(v.zoomPercent()); });
    loadedFor.current = null;
    cameraProject.current = null;
    let pressed: Level | undefined;
    v.setInteractionHandlers({
      onNavigationIntent: () => navigationIntent.current?.(),
      onPick: (hit, anchor, material, modifiers) => {
        if (editBlockedRef.current || !v.matchesScope(store.get().projectId,store.get().activeLevelIndex)) return;
        const tapped = selectionOfHit(hit, material);
        const current = store.get().selection;
        // In resize mode a tap on anything but another item means «done»: the mode ends and the
        // item stays selected, the next tap chooses again.
        if (store.get().resizeMode && !(tapped?.kind === 'item' && !(current?.kind === 'item' && current.id === tapped.id))) { store.setResizeMode(false); return; }
        // Shift gathers items, as on the plan: a tap on one adds it or takes it out, and a tap on
        // anything else leaves the gathered items alone.
        if (modifiers?.shiftKey && (tapped?.kind === 'item' || selectedItemIds(current).length)) {
          if (tapped?.kind === 'item') { const next = toggleItemSelection(current, tapped.id); store.select(next, next ? anchor : null); }
          return;
        }
        const next = nextSelectionOnTap(current, tapped);
        store.select(next, next ? anchor : null, next?.kind === 'item' || next?.kind === 'opening' ? material?.slot : undefined);
        // The app adds new furniture where the user last touched the floor.
        const floor = v.floorPointAt(anchor.x, anchor.y);
        if (floor) store.set({ lastFloorPoint: floor });
      },
      onMaterialSlots:(id,slots)=>{
        if(!v.matchesScope(store.get().projectId,store.get().activeLevelIndex))return;
        const selection=store.get().selection;
        if((selection?.kind==='item'||selection?.kind==='opening')&&selection.id===id)store.set({selectionMaterialSlots:slots});
      },
      // A finger has no hover to take a handle's highlight away again when it lets go.
      onGesture: next => { setGesture(next); store.setCameraDragging(next==='camera'); if (!next) setResizeHot(null); },
      onResizeModeEnd:()=>store.setResizeMode(false),
      onCopyPlaced: id => store.select({ kind: 'item', id }, null),
      onLockedStayed: count => store.notifyLockedStayed(count),
      copyIdentity: sourceId => store.newItemIdentity({ copyOf: sourceId }),
      // The walk brings the floor it climbed to into view; the viewer carries the walker onto it as it loads it.
      onWalkFloor: index => { store.setActiveLevel(index); if (store.get().activeLevelIndex === index) walkFloor.current?.(index); },
      onEditPrepare: () => { navigationIntent.current?.(); store.clearPropertiesPreview(); pressed = store.level; },
      onEditBegin: () => store.beginGeometryStroke(pressed),
      onEditPreview: level => { if(v.matchesScope(store.get().projectId,store.get().activeLevelIndex)) store.commitLevel('',()=>level); },
      onEditEnd: cancelled => { if (cancelled) store.cancelStroke(); else store.endStroke(); },
      onDoorMarkers: markers => setDoorMarkers(markers),
      onOverlay: next => {
        // The plan owns this shared overlay event while its canvas is visible.
        if (store.get().mode === 'plan' && store.get().planSurface !== 'wall') { setOverlay(null); return; }
        setOverlay(previous => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
        window.dispatchEvent(new CustomEvent('hp:selection-frame', { detail: next ? { ...next.frame, keepOut: next.keepOut } : null }));
      },
    });
    v.setEditingEnabled(!dragsBlockedRef.current);
    const unsubscribeHeld = followHeldDrag(store, () => v.cancelEdit());
    const unsubscribeCreations = store.subscribeItemCreations(event => v.queueItemAppearances(event.projectId, event.ids, event.levelIndex));
    // Reconcile the imperative model before the plan's input RAF paints the same
    // immutable document. React still owns chrome, never the timing of model poses.
    let loadedScene: EditorState['scene'] = null;
    let loadedCeiling = false;
    let loadedIsolated: number | null = null;
    let loadedPreview: EditorState['materialTransformPreview'] | undefined;
    const reconcile = () => {
      const current = store.get(), presented = store.presentedScene;
      const freshProject = loadedFor.current?.projectId !== current.projectId;
      const fresh = freshProject || loadedFor.current?.levelIndex !== current.activeLevelIndex;
      const changed = presented !== loadedScene || fresh || loadedCeiling !== current.showCeiling || loadedIsolated !== current.isolatedRoom;
      // Slot metadata can publish synchronously from load(). Set guards first.
      loadedScene = presented; loadedCeiling = current.showCeiling; loadedIsolated = current.isolatedRoom; loadedFor.current = { projectId: current.projectId, levelIndex: current.activeLevelIndex };
      if (changed && presented) {
        v.load(presented, {projectId:current.projectId,levelIndex:current.activeLevelIndex,
          showCeiling:current.showCeiling,wallMode:current.wallMode,keepCamera:!freshProject,isolatedRoom:current.isolatedRoom});
        setProgress(v.hydrationProgress());
        const wanted = current.firstPerson ? 'walk' : 'orbit';
        if (cameraProject.current !== current.projectId) {
          // The project's first frame decides where the camera stands: on the view it was
          // left on when there is one, else the default framing of this mode. The store's
          // mode wins — it was restored from the same record before the scene was loaded.
          // An invitation has none: the view kept here is the guest's own copy's.
          cameraProject.current = current.projectId;
          cameraApplied.current = wanted;
          const stored = current.edit !== 'all' ? null : readProjectView(current.projectId)?.cameras ?? null;
          if (!(stored && v.restoreCameraState(stored, wanted))) v.setCameraMode(wanted);
        } else if (fresh && v.cameraMode !== wanted) { cameraApplied.current = wanted; v.setCameraMode(wanted); }
      }
      if (changed || loadedPreview !== current.materialTransformPreview) {
        loadedPreview = current.materialTransformPreview;
        v.setMaterialTransformPreview(loadedPreview);
      }
    };
    const unsubscribeDocument = store.subscribe(reconcile);
    reconcile();
    store.surfaceProbe = v.surfaceProbe;
    if (import.meta.env.DEV) (window as unknown as { hp?: Viewer }).hp = v;
    const onExposure = (e: Event) => { v.setExposure((e as CustomEvent<number>).detail); };
    window.addEventListener('hp:exposure', onExposure);
    // The view out of the windows reports its pictures to the picker, which asks again for one that failed.
    const unsubscribeWindowView = v.onWindowViewStatus(status => store.set({ windowViewStatus: status }));
    const onRetryWindowView = () => v.retryWindowView();
    window.addEventListener('hp:retry-window-view', onRetryWindowView);
    const id = setInterval(() => {
      const s = v.stats();
      setStats(s as unknown as Record<string,number>);
      store.set({ stats: s as unknown as Record<string, number> });
      setProgress(v.hydrationProgress());
    }, 500);
    return () => {
      clearInterval(id);
      unsubscribeHeld();
      unsubscribeCreations();
      unsubscribeDocument();
      window.removeEventListener('hp:exposure', onExposure);
      unsubscribeWindowView(); window.removeEventListener('hp:retry-window-view', onRetryWindowView);
      unsubscribeZoom();
      v.cancelInteraction();
      v.dispose();
      window.dispatchEvent(new CustomEvent('hp:selection-frame', { detail: null }));
      if (store.surfaceProbe === v.surfaceProbe) store.surfaceProbe = null;
      viewerRef.current = null;
    };
  }, []);

  useEffect(() => { viewerRef.current?.setWallMode(state.wallMode); }, [state.wallMode]);
  useEffect(() => { viewerRef.current?.setWindowView(state.windowView, state.windowViewRotation); }, [state.windowView, state.windowViewRotation]);

  useEffect(() => {
    // `setCameraMode` re-frames when the mode is already the current one — that is what
    // "Центрировать" is for. On mount the load has just placed the camera, so only a real
    // change of mode may run through here.
    const wanted = state.firstPerson ? 'walk' : 'orbit';
    if (cameraApplied.current === wanted) return;
    cameraApplied.current = wanted;
    viewerRef.current?.setCameraMode(wanted);
  }, [state.firstPerson]);

  // The demo walkthrough's door buttons are followed only while it is on and the scene is in view.
  useEffect(() => { viewerRef.current?.setWalkthroughDoors(state.walkthrough && !hidden); }, [state.walkthrough, hidden]);

  // Selection is shared between the plan and the scene.
  useEffect(() => {
    const selection = state.selection;
    viewerRef.current?.highlight(selection?.kind === 'item' ? selection.id : null);
    viewerRef.current?.gather(selection?.kind === 'items' ? selection.ids : []);
    viewerRef.current?.selectOpening(selection?.kind === 'opening' ? selection.id : null);
    // A plinth's mark covers its colour: while its colours are being chosen, the strip shows as it is.
    viewerRef.current?.selectTrim(selection?.kind === 'trim' && state.materialTarget?.kind !== 'trim' ? { wallId: selection.wallId, trim: selection.trim } : null);
    viewerRef.current?.selectSurface(selection?.kind === 'wall' ? { kind: 'wall', id: selection.id }
      : selection?.kind === 'room' ? { kind: 'room', index: selection.index } : null);
  }, [state.selection, state.materialTarget?.kind]);

  useEffect(() => {
    if (!hidden) viewerRef.current?.resize();
    viewerRef.current?.setActive(!hidden);
  }, [hidden]);

  // The canvas changes size without the window too: the chat column narrows the stage beside it.
  // A hidden canvas measures nothing and is resized when it shows again.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => { if (!hiddenRef.current && canvas.clientWidth > 0 && canvas.clientHeight > 0) viewerRef.current?.resize(); });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  // The wall plan: a plane of the 2D plan that this canvas draws through an orthographic camera.
  useEffect(() => {
    viewerRef.current?.setWallPlan(state.mode === 'plan' && state.planSurface === 'wall' ? state.wallView : null);
  }, [state.mode, state.planSurface, state.wallView]);

  useEffect(() => { viewerRef.current?.setEditingEnabled(!dragsBlocked); }, [dragsBlocked]);
  useEffect(() => { viewerRef.current?.setResizeMode(state.resizeMode); }, [state.resizeMode]);

  useImperativeHandle(ref, () => ({
    cancelInteraction: () => viewerRef.current?.cancelInteraction(),
    contextPressAt: (x, y) => {
      const v = viewerRef.current;
      if (!v) return { point: null, decided: false };
      v.cancelInteraction();
      const point = v.floorPointAt(x, y);
      if (editBlockedRef.current || !v.matchesScope(store.get().projectId, store.get().activeLevelIndex)) return { point, decided: false };
      const { hit, material } = v.pickAt(x, y), next = contextSelection(store.get().selection, selectionOfHit(hit, material));
      store.select(next, next ? { x, y } : null, next?.kind === 'item' || next?.kind === 'opening' ? material.slot : undefined);
      return { point, decided: JSON.stringify(store.get().selection) === JSON.stringify(next) };
    },
    planLayer: () => viewerRef.current?.planLayer() ?? null,
    surfaceHeight: Object.assign((item: Item, points: readonly Vec2[]) => viewerRef.current?.surfaceProbe(item, points) ?? null,
      { layers: (item: Item, points: readonly Vec2[]) => viewerRef.current?.surfaceProbe.layers?.(item, points) ?? null }),
    placementPointer: (x, y) => viewerRef.current?.placementPointer(x, y),
    shelfPointAt: (item, x, y) => viewerRef.current?.shelfPointAt(item, x, y) ?? null,
    recenter: () => { navigationIntent.current?.(); viewerRef.current?.setCameraMode('orbit'); },
    capture: () => viewerRef.current?.capture(store.get().projectName),
    snapshot: (maxSide, crop) => viewerRef.current?.snapshot(maxSide, 'image/jpeg', 0.9, crop) ?? Promise.resolve(null),
    canvasElement: () => canvasRef.current,
    setZoom: (factor) => viewerRef.current?.setZoom(factor),
    cameraPose: () => viewerRef.current?.cameraPose() ?? null,
    cameraState: () => viewerRef.current?.cameraState() ?? null,
    participantViewport: () => {
      const viewer = viewerRef.current, current = store.get();
      return viewer?.matchesScope(current.projectId, current.activeLevelIndex) ? viewer.participantViewport() : null;
    },
    applyParticipantViewport: viewport => {
      const viewer = viewerRef.current, current = store.get();
      return !!viewer?.matchesScope(current.projectId, current.activeLevelIndex) && !hiddenRef.current && viewer.applyParticipantViewport(viewport);
    },
    releaseParticipantViewport: () => viewerRef.current?.releaseParticipantViewport(),
    previewFrame: (maxSide, background) => viewerRef.current?.snapshot(maxSide, 'image/jpeg', 0.72, undefined, background) ?? Promise.resolve(null),
    onFrame: (listener) => viewerRef.current?.onFrame(listener) ?? (() => {}),
    navigateToRoom: (room) => viewerRef.current?.navigateToRoom(room),
    enterThroughDoor: (openingId, room) => viewerRef.current?.enterThroughDoor(openingId, room),
    goStairs: direction => viewerRef.current?.goStairs(direction) ?? false,
    floorPointAt: (x, y) => viewerRef.current?.floorPointAt(x, y) ?? null,
    wallPointAt: (x, y) => viewerRef.current?.wallPointAt(x, y) ?? null,
    worldPointAt: (x, y) => viewerRef.current?.worldPointAt(x, y) ?? null,
    setParticipantSelections: values => viewerRef.current?.setParticipantSelections(values),
    screenPointAt: (point, height, bounds) => viewerRef.current?.screenPointAt(point, height, bounds) ?? null,
    revealPoint: (point, area, height) => viewerRef.current?.revealPoint(point, area, height) ?? false,
    wallPlanFrame: () => viewerRef.current?.wallPlanFrame() ?? null,
    wallPlanMarks: () => viewerRef.current?.wallPlanMarks() ?? null,
    resetWallPlanView: () => viewerRef.current?.resetWallPlanView(),
    zoomView: factor => viewerRef.current?.zoomView(factor),
    setWallPlanInsets: (insets) => viewerRef.current?.setWallPlanInsets(insets) ?? false,
  }), []);

  const rotate = overlay?.rotate;
  const canvasRect = canvasRef.current?.getBoundingClientRect();
  const left = canvasRect?.left ?? 0, top = canvasRect?.top ?? 0;
  const hoveredAxis = useResizeHot();
  useEffect(() => { if (!state.resizeMode) setResizeHot(null); }, [state.resizeMode]);
  const resizedAxis = useRef<'w' | 'd' | 'h' | undefined>(undefined);
  resizedAxis.current = overlay?.resize?.find(handle => handle.active)?.axis;
  useEffect(() => {
    if (gesture !== 'rotate' && gesture !== 'resize') return;
    let frame = 0, x = 0, y = 0, fine = false;
    const paint = () => {
      frame = 0;
      const selection = store.get().selection, item = selection?.kind === 'item' ? store.presentedLevel?.items.find(value => value.id === selection.id) : undefined;
      if (!fine || !item) { hideGestureChip(); return; }
      showGestureChip(chipText(itemChip(gesture, item, resizedAxis.current)), x, y);
    };
    // Capture: the canvas stops the move it handles, and the preview it commits is read on the next frame.
    const move = (event: PointerEvent) => { fine = event.pointerType === 'mouse' || event.pointerType === 'pen'; x = event.clientX; y = event.clientY; frame ||= requestAnimationFrame(paint); };
    window.addEventListener('pointermove', move, true);
    return () => { window.removeEventListener('pointermove', move, true); cancelAnimationFrame(frame); hideGestureChip(); };
  }, [gesture]);
  // The dragged face wins; otherwise whatever handle or size the pointer, a finger or the keyboard is on.
  const hotAxis = state.resizeMode ? overlay?.resize?.find(handle => handle.active)?.axis ?? hoveredAxis : null;
  const hotHandle = hotAxis ? overlay?.resize?.find(handle => handle.axis === hotAxis) : undefined;
  // A size whose own side of the handle lies under a panel or off the stage moves to the other side, over the face.
  const resizing = !editBlocked && !itemsOnly && state.resizeMode && !state.selectedLocked;
  const chrome = resizing && overlay?.labels?.length ? chromeRects() : [];
  const clearBox = (box: { x: number; y: number; width: number; height: number }) => {
    const stage = canvasRect;
    return (!stage || (box.x >= stage.left && box.y >= stage.top && box.x + box.width <= stage.right && box.y + box.height <= stage.bottom))
      && !chrome.some(panel => box.x < panel.x + panel.width && panel.x < box.x + box.width && box.y < panel.y + panel.height && panel.y < box.y + box.height);
  };
  const clear = (label: NonNullable<ViewerOverlay['labels']>[number]) => clearBox(resizeLabelRect(label));
  // Each size on its dimension line, the first edge whose size nothing covers; an axis with no line
  // (its edges too short on screen) keeps the size beside its handle.
  const dims = overlay?.dims?.map(dim => ({ ...dim, line: dim.candidates.find(line => clearBox(resizeChipRect(line, dim.text))) ?? dim.candidates[0] }));
  const labels = overlay?.labels?.filter(label => !dims?.some(dim => dim.axis === label.axis))
    .map(label => clear(label) || !clear(flippedResizeLabel(label)) ? label : flippedResizeLabel(label));
  /** A size on the stage goes to its number in the resize bar. */
  const typeSize = (axis: 'w' | 'h' | 'd') => document.querySelector<HTMLInputElement>(`.context-bar .cb-size-input[data-axis="${axis}"]`)?.focus();
  /** Arrow keys on a focused handle: up or right push its face out, down or left pull it in; 1 cm, 10 with Shift.
   * A run of presses on one face is one undo step. */
  const nudge = (face: { axis: 'w' | 'h' | 'd'; side: -1 | 1 }, event: ReactKeyboardEvent) => {
    const grow = event.key === 'ArrowUp' || event.key === 'ArrowRight', shrink = event.key === 'ArrowDown' || event.key === 'ArrowLeft';
    if (!grow && !shrink) return;
    event.preventDefault(); event.stopPropagation();
    const viewer = viewerRef.current, item = store.selectedItem(), level = store.level;
    if (!viewer || !item || !level || state.isDragging) return;
    store.clearPropertiesPreview();
    const next = previewItemResize(level, item.id, viewer.itemResizeBounds(item), face, (grow ? 1 : -1) * face.side * (event.shiftKey ? .1 : .01));
    if (next !== level) store.commitLevel(`resize-keys:${item.id}:${face.axis}:${face.side}`, () => next);
  };
  /** Arrow keys on a focused handle of an opening: up or right push its edge out, down or left pull it in; 1 cm, 10
   * with Shift. A run of presses on one edge is one undo step. */
  const nudgeOpening = (edge: OpeningEdge, event: ReactKeyboardEvent) => {
    const grow = event.key === 'ArrowUp' || event.key === 'ArrowRight', shrink = event.key === 'ArrowDown' || event.key === 'ArrowLeft';
    if (!grow && !shrink) return;
    event.preventDefault(); event.stopPropagation();
    const level = store.level, selection = state.selection;
    const opening = selection?.kind === 'opening' ? level?.openings.find(value => value.id === selection.id) : undefined;
    if (!level || !opening || state.isDragging) return;
    const step = (grow ? 1 : -1) * (event.shiftKey ? .1 : .01), sides = edgeSides(edge), point = edgePoint(openingBox(opening), edge);
    const target = { t: point.t + (sides.left ? -step : sides.right ? step : 0), h: point.h + (sides.top ? step : sides.bottom ? -step : 0) };
    store.clearPropertiesPreview();
    const result = previewOpeningEdge(level, opening.id, edge, target, { free: true });
    if (result.error) store.set({ error: result.error });
    else if (result.level && result.level !== level) store.commitLevel(`opening-resize-keys:${opening.id}:${edge}`, () => result.level!);
  };
  const typeOpeningSize = (kind: OpeningDimension) => document.querySelector<HTMLInputElement>(`.context-bar .cb-size-input[data-opening="${kind}"]`)?.focus();
  const showRotate = !editBlocked && !state.resizeMode && !state.selectedLocked && state.selection?.kind === 'item' && rotate && (!state.isDragging || gesture === 'rotate');
  // The app's HeightFromFloorWithDragButton: a piece on the floor or under the ceiling rides up and down with it.
  const heightAt = overlay?.height;
  const showHeight = !editBlocked && !itemsOnly && !state.resizeMode && !state.selectedLocked && state.selection?.kind === 'item' && !!heightAt && (!state.isDragging || gesture === 'height');
  const lift = (event: ReactKeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); store.set({ showProperties: true }); return; }
    const up = event.key === 'ArrowUp', down = event.key === 'ArrowDown';
    if (!up && !down) return;
    event.preventDefault(); event.stopPropagation();
    const item = store.selectedItem(), level = store.level;
    if (!item || !level || state.isDragging) return;
    const max = Math.max(item.elevation, level.height - item.size.h);
    const elevation = Math.round(Math.max(0, Math.min(max, item.elevation + (up ? 1 : -1) * (event.shiftKey ? .1 : .01))) * 1000) / 1000;
    if (elevation === item.elevation) return;
    store.clearPropertiesPreview();
    store.commitLevel(`height-keys:${item.id}`, value => previewSceneItemTransform(value, item.id, { position: item.position, rotation: item.rotation, elevation }, viewerRef.current?.surfaceProbe));
  };
  const lifted = state.selection?.kind === 'item' ? store.selectedItem()?.elevation : undefined;
  const loadingModels = !!progress?.pending;
  const loadingPercent = progress?.total ? Math.floor(100 * (progress.total - progress.pending) / progress.total) : 0;
  // An item the catalog has no model for is not a problem the customer can act on: the scene
  // shows its translucent shape and the status pill retires, the same as a clean load.
  const loadingComplete = progress?.status === 'complete';

  return <>
    <canvas ref={canvasRef} className="scene-canvas" style={{ display: hidden ? 'none' : 'block' }} data-gesture={gesture ?? 'idle'} />
    {resizing&&overlay?.box&&<svg className="scene-resize-box" aria-hidden="true">
      {overlay.box.map((edge,i)=><line key={i} data-hidden={edge.hidden||undefined} x1={edge.x1-left} y1={edge.y1-top} x2={edge.x2-left} y2={edge.y2-top}/>)}
      {overlay.resize?.map(handle=>handle.anchor&&<line key={`leader:${handle.axis}:${handle.side}`} className="scene-resize-leader" x1={handle.anchor.x-left} y1={handle.anchor.y-top} x2={handle.x-left} y2={handle.y-top}/>)}
      {overlay.ghost?.map((edge,i)=><line key={`ghost:${i}`} className="scene-resize-ghost" x1={edge.x1-left} y1={edge.y1-top} x2={edge.x2-left} y2={edge.y2-top}/>)}
      {dims?.map(({axis,line})=>{const o=RESIZE_DIM_OFFSET,{x1,y1,x2,y2,n}=line,ax=x1-left,ay=y1-top,bx=x2-left,by=y2-top;
        return <g key={`dim:${axis}`} className="scene-resize-dim" data-hot={hotAxis===axis||undefined} data-dim={(!!hotAxis&&hotAxis!==axis)||undefined}>
          <line x1={ax+n.x*4} y1={ay+n.y*4} x2={ax+n.x*(o+5)} y2={ay+n.y*(o+5)}/><line x1={bx+n.x*4} y1={by+n.y*4} x2={bx+n.x*(o+5)} y2={by+n.y*(o+5)}/>
          <line x1={ax+n.x*o} y1={ay+n.y*o} x2={bx+n.x*o} y2={by+n.y*o}/>
          <circle cx={ax+n.x*o} cy={ay+n.y*o} r="2.2"/><circle cx={bx+n.x*o} cy={by+n.y*o} r="2.2"/></g>;})}
      {hotHandle?.outline.length===4&&<polygon className="scene-resize-face" points={hotHandle.outline.map(point=>`${point.x-left},${point.y-top}`).join(' ')}/>}
    </svg>}
    {resizing&&dims?.map(({axis,line,text,delta})=>{const at=resizeDimPoint(line);
      return <button key={`size:${axis}`} type="button" className="scene-resize-label scene-resize-chip" data-axis={axis} data-hot={hotAxis===axis||undefined} data-dim={(!!hotAxis&&hotAxis!==axis)||undefined}
        style={{left:at.x-left,top:at.y-top}} aria-label={t('#EditorSizeTypeExactLabel', RESIZE_AXIS_NAME[axis], text)} title={t('#EditorSizeTypeExactTitle', RESIZE_AXIS_NAME[axis])}
        onPointerEnter={event=>{if(event.pointerType==='mouse')setResizeHot(axis);}} onPointerLeave={event=>{if(event.pointerType==='mouse')releaseResizeHot(axis);}}
        onPointerDown={event=>event.stopPropagation()} onClick={()=>typeSize(axis)}>
        <b>{RESIZE_AXIS_LETTER[axis]}</b>{text}{delta?<em>{delta>0?'+':'−'}{Math.abs(delta)}</em>:null}</button>;})}
    {resizing&&labels?.map(label=>{const shift=resizeLabelShift(label.dir);
      return <span key={label.axis} className="scene-resize-label" data-axis={label.axis} data-hot={hotAxis===label.axis||undefined} data-dim={(!!hotAxis&&hotAxis!==label.axis)||undefined}
        style={{left:label.x-left,top:label.y-top,transform:`translate(${shift.x*100}%,${shift.y*100}%)`}}><b>{RESIZE_AXIS_LETTER[label.axis]}</b>{label.text}</span>;})}
    {resizing&&overlay?.resize?.map(handle=>{const size=overlay.labels?.find(label=>label.axis===handle.axis)?.text;
      return <button key={`${handle.axis}:${handle.side}`} type="button"
      className="scene-resize-handle" data-axis={handle.axis} data-side={handle.side} data-active={handle.active} data-hot={hotAxis===handle.axis||undefined}
      aria-label={size?t('#EditorResizeHandleSizeLabel',RESIZE_AXIS_NAME[handle.axis],size):t('#EditorResizeHandleLabel',RESIZE_AXIS_NAME[handle.axis])}
      title={t('#EditorResizeHandleTitle',RESIZE_AXIS_NAME[handle.axis])}
      style={{left:handle.x-left,top:handle.y-top,'--resize-cursor':cursorStyle(handleResize(handle.angle))} as CSSProperties}
      onPointerEnter={event=>{if(event.pointerType==='mouse')setResizeHot(handle.axis);}}
      onPointerLeave={event=>{if(event.pointerType==='mouse'&&document.activeElement!==event.currentTarget)releaseResizeHot(handle.axis);}}
      onFocus={()=>setResizeHot(handle.axis)} onBlur={()=>releaseResizeHot(handle.axis)}
      onPointerDown={event=>{if(event.button!==0)return;event.preventDefault();event.stopPropagation();setResizeHot(handle.axis);
        viewerRef.current?.beginResize(handle,{clientX:event.clientX,clientY:event.clientY,pointerId:event.pointerId,pointerType:event.pointerType});}}
      onKeyDown={event=>nudge(handle,event)}
    ><svg className="scene-resize-arrow" viewBox="-8 -8 16 16" aria-hidden="true" style={{transform:`rotate(${handle.angle-90}deg)`}}>
      <path d="M-5.5 0H5.5M-5.5 0l2.8-2.8M-5.5 0l2.8 2.8M5.5 0l-2.8-2.8M5.5 0l-2.8 2.8"/></svg></button>;})}
    {resizing&&overlay?.opening&&<svg className="scene-resize-box scene-opening-box" aria-hidden="true">
      {overlay.opening.ghost&&<polygon className="scene-resize-ghost-face" points={overlay.opening.ghost.map(point=>`${point.x-left},${point.y-top}`).join(' ')}/>}
      <polygon className="scene-opening-outline" points={overlay.opening.outline.map(point=>`${point.x-left},${point.y-top}`).join(' ')}/>
      {overlay.opening.dims.map(({kind,line})=>{const o=OPENING_DIM_OFFSET,{x1,y1,x2,y2,n}=line,ax=x1-left,ay=y1-top,bx=x2-left,by=y2-top;
        return <g key={`opening-dim:${kind}`} className="scene-resize-dim">
          <line x1={ax+n.x*4} y1={ay+n.y*4} x2={ax+n.x*(o+5)} y2={ay+n.y*(o+5)}/><line x1={bx+n.x*4} y1={by+n.y*4} x2={bx+n.x*(o+5)} y2={by+n.y*(o+5)}/>
          <line x1={ax+n.x*o} y1={ay+n.y*o} x2={bx+n.x*o} y2={by+n.y*o}/>
          <circle cx={ax+n.x*o} cy={ay+n.y*o} r="2.2"/><circle cx={bx+n.x*o} cy={by+n.y*o} r="2.2"/></g>;})}
    </svg>}
    {resizing&&overlay?.opening?.dims.map(({kind,line,text,delta})=>{const at=openingChipPoint(line,text);
      return <button key={`opening-size:${kind}`} type="button" className="scene-resize-label scene-resize-chip" data-opening={kind}
        style={{left:at.x-left,top:at.y-top}} aria-label={t('#EditorSizeTypeExactLabel', OPENING_DIMENSION_NAME[kind], text)} title={t('#EditorSizeTypeExactTitle', OPENING_DIMENSION_NAME[kind])}
        onPointerDown={event=>event.stopPropagation()} onClick={()=>typeOpeningSize(kind)}>
        <b>{OPENING_DIMENSION_LETTER[kind]}</b>{text}{delta?<em>{delta>0?'+':'−'}{Math.abs(delta)}</em>:null}</button>;})}
    {resizing&&overlay?.opening?.handles.map(handle=><button key={`opening:${handle.edge}`} type="button"
      className="scene-resize-handle" data-edge={handle.edge} data-active={handle.active}
      aria-label={t('#EditorResizeHandleLabel',OPENING_EDGE_NAME[handle.edge])}
      title={t('#EditorResizeHandleTitle',OPENING_EDGE_NAME[handle.edge])}
      style={{left:handle.x-left,top:handle.y-top,'--resize-cursor':cursorStyle(handleResize(handle.angle))} as CSSProperties}
      onPointerDown={event=>{if(event.button!==0)return;event.preventDefault();event.stopPropagation();
        viewerRef.current?.beginOpeningResize(handle,{clientX:event.clientX,clientY:event.clientY,pointerId:event.pointerId,pointerType:event.pointerType});}}
      onKeyDown={event=>nudgeOpening(handle.edge,event)}
    ><svg className="scene-resize-arrow" viewBox="-8 -8 16 16" aria-hidden="true" style={{transform:`rotate(${handle.angle-90}deg)`}}>
      <path d="M-5.5 0H5.5M-5.5 0l2.8-2.8M-5.5 0l2.8 2.8M5.5 0l-2.8-2.8M5.5 0l-2.8 2.8"/></svg></button>)}
    {showRotate && <button
      className="scene-rotate glass" aria-label={t('Rotate')} title={t('#EditorRotateTooltip')}
      data-active={gesture === 'rotate'}
      style={{ left: rotate.x - (canvasRect?.left ?? 0), top: rotate.y - (canvasRect?.top ?? 0),
        cursor: overlay && cursorStyle('rotate', rotateGlyphAngle(overlay.center.x, overlay.center.y, rotate.x, rotate.y)) }}
      onPointerDown={event => {
        if (event.button !== 0) return;
        event.preventDefault(); event.stopPropagation();
        viewerRef.current?.beginRotation({ clientX: event.clientX, clientY: event.clientY, pointerId: event.pointerId, pointerType: event.pointerType });
      }}
      onClick={event => {
        if (event.detail !== 0) return;
        const item = store.selectedItem();
        if (item) { store.rotateItem(item.id, item.rotation + Math.PI / 2); store.endStroke(); }
      }}
    ><Icon name="rotateHandle" size={22} /></button>}
    {showHeight && heightAt && <button
      className="scene-height glass" aria-label={lifted !== undefined ? t('#EditorHeightHandleValueLabel', t('#ValueCentimeters', Math.round(lifted * 100))) : t('#EditorHeightHandleLabel')}
      title={t('#EditorHeightTooltip')}
      data-active={gesture === 'height'}
      style={{ left: heightAt.x - (canvasRect?.left ?? 0), top: heightAt.y - (canvasRect?.top ?? 0) }}
      onPointerDown={event => {
        if (event.button !== 0) return;
        event.preventDefault(); event.stopPropagation();
        viewerRef.current?.beginHeight({ clientX: event.clientX, clientY: event.clientY, pointerId: event.pointerId, pointerType: event.pointerType });
      }}
      onKeyDown={lift}
    ><Icon name="heightDrag" size={22} />{gesture === 'height' && lifted !== undefined && <span className="scene-height-value">{t('#ValueCentimeters', Math.round(lifted * 100))}</span>}</button>}
    {/* The app's EnterRoomDoorMarkers in its demo walkthrough: a round button on each doorway in view. */}
    {state.walkthrough && !hidden && doorMarkers.map(marker => <button key={marker.id} type="button" className="walkthrough-door glass"
      aria-label={t('#EnterRoom')} data-tip={t('#EnterRoom')} style={{ left: marker.x - (canvasRect?.left ?? 0), top: marker.y - (canvasRect?.top ?? 0) }}
      onPointerDown={event => event.stopPropagation()}
      onClick={() => { const room = store.level?.rooms.find(value => value.index === marker.roomIndex); if (room) viewerRef.current?.enterThroughDoor(marker.id, room); }}>
      <Icon name="enterRoom" size={24} /></button>)}
    {!hidden && touchScreen && state.firstPerson && !state.wallView && <WalkStick onPush={(fwd, strafe) => viewerRef.current?.setWalkStick(fwd, strafe)} />}
    {statusSlot && createPortal(<aside
      className={`model-loading-status${loadingComplete ? ' is-complete' : ''}`}
      aria-label={t('#EditorModelLoadingStatus')}
      data-hydration-state={progress?.status ?? 'idle'}
      data-models-total={progress?.total ?? 0}
      data-models-loaded={progress?.loaded ?? 0}
      data-models-pending={progress?.pending ?? 0}
      data-models-failed={progress?.failed ?? 0}
      data-models-missing={progress?.missing ?? 0}
      data-items-loaded={progress?.itemsLoaded ?? 0}
      data-openings-loaded={progress?.openingsLoaded ?? 0}
      data-procedural-loaded={progress?.proceduralLoaded ?? 0}
      data-render-frames={stats.renderFrames ?? 0}
      data-ao-frames={stats.aoFrames ?? 0}
      data-plan-render-frames={stats.planRenderFrames ?? 0}
    >
      <div role="status" aria-live="polite">
        {loadingModels ? <div className="model-loading-row" role="progressbar" aria-label={t('#EditorItemsLoadingProgress')}
          aria-valuemin={0} aria-valuemax={100} aria-valuenow={loadingPercent}>
          <span className="model-loading-spinner" aria-hidden="true" />
          <span>{t('#EditorItemsLoading')}</span><span className="model-loading-percent">{loadingPercent}%</span>
        </div>
          : progress?.failed ? t('#EditorModelsNotLoaded', progress.failed)
          : progress?.status === 'complete' ? (progress.proceduralLoaded ? t('#EditorSceneReadyPanels', progress.itemsLoaded, progress.openingsLoaded, progress.proceduralLoaded) : t('#EditorSceneReady', progress.itemsLoaded, progress.openingsLoaded))
          : <div className="model-loading-row"><span className="model-loading-spinner" aria-hidden="true" /><span>{t('#EditorOpeningScene')}</span></div>}
      </div>
      {!!progress?.failed && <>
        {loadingModels && <p className="model-loading-error">{t('#EditorModelsNotLoadedCount', progress.failed)}</p>}
        <button onClick={() => { viewerRef.current?.retryModels(); setProgress(viewerRef.current?.hydrationProgress() ?? null); }}>
          {t('#EditorRetryModels')}
        </button>
        <details>
          <summary>{t('#EditorErrorDetails')}</summary>
          <ul>
            {progress.errors.map((error) => <li key={error.key}>{error.label}: {error.message}</li>)}
          </ul>
        </details>
      </>}
    </aside>, statusSlot)}
  </>;
});
